import { useMemo } from "react";
import { Appointment, useAppointmentsContext } from "../state/appointments";

export {
  DEFAULT_DURATION_MINUTES,
  MIN_DURATION_MINUTES
} from "../state/appointments";

// Consultations started up to 1h ago still count as "current", not history.
// Only used as a fallback when the backend `state` is missing (NONE/null).
const PAST_CUTOFF_MS = 60 * 60 * 1000;

// Single source of truth for appointment statuses — avoid scattering the raw
// strings across the codebase.
export const APPOINTMENT_STATUS = {
  Scheduled: "scheduled",
  Done: "done",
  Cancelled: "cancelled",
  /** Scheduled time came and went without the consultation ever starting. */
  Lapsed: "lapsed"
} as const;

export type AppointmentStatus = typeof APPOINTMENT_STATUS[keyof typeof APPOINTMENT_STATUS];

// Backend `state` codes (VideoCallAppointmentState), confirmed with the API team.
export const APPOINTMENT_STATE = {
  None: 0,
  Scheduled: 1,
  OnGoing: 2,
  Finished: 3,
  Cancelled: 4,
  Lapsed: 5
} as const;

/**
 * Realizada / Cancelada / Expirada / Agendada. The backend `state` is the source
 * of truth; the schedule date is only a fallback when no state is set (NONE/null),
 * e.g. legacy rows. This keeps a FINISHED consultation in history even when its
 * scheduled time hasn't passed yet (finished early), and vice-versa.
 */
export function getAppointmentStatus(
  appointment: Appointment
): AppointmentStatus {
  // Locally cancelled this session, or the backend CANCELLED state.
  if (
    appointment.cancelled ||
    appointment.state === APPOINTMENT_STATE.Cancelled
  )
    return APPOINTMENT_STATUS.Cancelled;

  switch (appointment.state) {
    case APPOINTMENT_STATE.Finished:
      return APPOINTMENT_STATUS.Done;
    // Never started and the slot passed → history, but not "realizada".
    case APPOINTMENT_STATE.Lapsed:
      return APPOINTMENT_STATUS.Lapsed;
    // Still to happen, or happening right now → upcoming.
    case APPOINTMENT_STATE.Scheduled:
    case APPOINTMENT_STATE.OnGoing:
      return APPOINTMENT_STATUS.Scheduled;
  }

  // No usable state (NONE/null) → fall back to the schedule date.
  return appointment.start.getTime() < Date.now() - PAST_CUTOFF_MS
    ? APPOINTMENT_STATUS.Done
    : APPOINTMENT_STATUS.Scheduled;
}

/**
 * Reads appointments from the shared AppointmentsProvider. Optionally filters by
 * clinical file (the appointment's identity) and derives the upcoming/past split.
 *
 * Appointments are oriented around `clinicalFileId`; the patient name is resolved
 * from the doctor's clinical-file list inside the provider.
 */
export function useAppointments(clinicalFileId?: string) {
  const {
    appointments: all,
    addAppointment,
    cancelAppointment,
    refresh
  } = useAppointmentsContext();

  const appointments = useMemo(
    () =>
      clinicalFileId
        ? all.filter(a => a.clinicalFileId === clinicalFileId)
        : all,
    [all, clinicalFileId]
  );

  const { upcoming, past } = useMemo(() => {
    // Split by status (state-driven), not by date: a FINISHED/CANCELLED/LAPSED
    // consultation belongs to history even if its scheduled time is still in
    // the future, and a SCHEDULED/ON_GOING one stays upcoming.
    const up = appointments
      .filter(a => getAppointmentStatus(a) === APPOINTMENT_STATUS.Scheduled)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    const pa = appointments
      .filter(a => getAppointmentStatus(a) !== APPOINTMENT_STATUS.Scheduled)
      .sort((a, b) => b.start.getTime() - a.start.getTime());
    return { upcoming: up, past: pa };
  }, [appointments]);

  return {
    appointments,
    upcoming,
    past,
    addAppointment,
    deleteAppointment: cancelAppointment,
    refresh
  };
}
